import {formatDateTime} from '../time';
import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from './Layout';
import RefreshIconButton from './RefreshIconButton';
import { useOrganization } from '../context/OrganizationContext';

function formatDate(value) { return formatDateTime(value, '—'); }

function badgeClass(value) {
  const v = String(value || '').toLowerCase();
  if (v === 'critical' || v === 'high') return 'bg-red-50 text-red-700 ring-red-200';
  if (v === 'medium') return 'bg-amber-50 text-amber-700 ring-amber-200';
  if (v === 'low') return 'bg-sky-50 text-sky-700 ring-sky-200';
  return 'bg-slate-50 text-slate-700 ring-slate-200';
}

export default function SyslogPage({ onLogout }) {
  const { activeOrganization, organizationLoading, organizationError } = useOrganization();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    const organizationGuid = activeOrganization?.guid;
    if (!organizationGuid) {
      setAlerts([]);
      setSummary(null);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required.');
      return;
    }

    const baseUrl = process.env.REACT_APP_CYBEDEFENDER_AI_URL || '/api';
    const scope = `organization_guid=${encodeURIComponent(organizationGuid)}`;
    const headers = { Authorization: `Bearer ${token}` };

    try {
      setLoading(true);
      setError('');
      const [alertsRes, summaryRes] = await Promise.all([
        fetch(`${baseUrl}/cybedefender/syslog-alerts?${scope}&limit=100`, { headers }),
        fetch(`${baseUrl}/cybedefender/syslog-summary?${scope}`, { headers })
      ]);

      if (!alertsRes.ok) throw new Error(`Alerts request failed: ${alertsRes.status}`);
      if (!summaryRes.ok) throw new Error(`Summary request failed: ${summaryRes.status}`);

      const alertsData = await alertsRes.json();
      const summaryData = await summaryRes.json();
      setAlerts(Array.isArray(alertsData) ? alertsData : []);
      setSummary(summaryData || null);
    } catch (loadError) {
      setAlerts([]);
      setSummary(null);
      setError(loadError.message || 'Failed to load syslog telemetry.');
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.guid]);

  useEffect(() => {
    if (!activeOrganization?.guid) return undefined;
    loadData();
    const timer = setInterval(loadData, 15000);
    return () => clearInterval(timer);
  }, [activeOrganization?.guid, loadData]);

  return (
    <AppLayout onLogout={onLogout} headerActions={<RefreshIconButton onClick={loadData} disabled={!activeOrganization} loading={loading} label="Refresh syslog"/>}>
      <div className="space-y-6">

        {(organizationError || error) && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {organizationError || error}
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-slate-500">Syslog Events</div>
            <div className="mt-2 text-3xl font-semibold text-slate-950">{summary?.total_events ?? 0}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-slate-500">Security Alerts</div>
            <div className="mt-2 text-3xl font-semibold text-slate-950">{summary?.total_alerts ?? 0}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-slate-500">Open Alerts</div>
            <div className="mt-2 text-3xl font-semibold text-slate-950">{summary?.open_alerts ?? 0}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-medium text-slate-500">Latest Event</div>
            <div className="mt-2 text-sm font-semibold text-slate-900">
              {formatDate(summary?.latest_event?.timestamp)}
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4">
            <h3 className="font-semibold text-slate-900">Recent collector alerts</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Time</th>
                  <th className="px-5 py-3">Threat</th>
                  <th className="px-5 py-3">Severity</th>
                  <th className="px-5 py-3">Source IP</th>
                  <th className="px-5 py-3">Source Device</th>
                  <th className="px-5 py-3">Collector</th>
                  <th className="px-5 py-3">Score</th>
                  <th className="px-5 py-3">Rule</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {organizationLoading || loading ? (
                  <tr><td colSpan="9" className="px-5 py-8 text-center text-slate-500">Loading telemetry...</td></tr>
                ) : alerts.length === 0 ? (
                  <tr><td colSpan="9" className="px-5 py-8 text-center text-slate-500">No syslog alerts found for this organization.</td></tr>
                ) : alerts.map((item) => (
                  <tr key={item.alert_id} className="border-t border-slate-100">
                    <td className="px-5 py-4 text-slate-600">{formatDate(item.created_at)}</td>
                    <td className="px-5 py-4 font-medium text-slate-900">{item.threat_type || '-'}</td>
                    <td className="px-5 py-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${badgeClass(item.severity)}`}>{item.severity || '-'}</span></td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-700">{item.client_ip || '-'}</td>
                    <td className="px-5 py-4 text-slate-600">{item.source_device || '-'}</td>
                    <td className="px-5 py-4 text-slate-600">{item.collector_device_name || '-'}</td>
                    <td className="px-5 py-4 text-slate-700">{item.threat_score ?? 0}</td>
                    <td className="px-5 py-4 text-slate-600">{item.matched_rule || '-'}</td>
                    <td className="px-5 py-4 text-slate-600">{item.status || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
